import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-store-config-products-tab',
  templateUrl: './store-config-products-tab.component.html',
  styleUrls: ['./store-config-products-tab.component.scss']
})
export class StoreConfigProductsTabComponent implements OnInit, OnChanges {
  @Input() storeId!: string;

  productCount: number = 0;
  countLoading: boolean = false;
  countError: string | null = null;
  deleteInProgress: boolean = false;

  constructor(private ecommerceService: EcommerceService) {}

  ngOnInit(): void {
    if (this.storeId) {
      this.loadProductCount();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const storeIdChange = changes['storeId'];
    if (!storeIdChange || storeIdChange.firstChange) {
      return;
    }
    const prev = storeIdChange.previousValue;
    const curr = storeIdChange.currentValue;
    if (prev !== curr && curr) {
      this.productCount = 0;
      this.countError = null;
      this.loadProductCount();
    }
  }

  get canDeleteAll(): boolean {
    return !this.countLoading && !this.countError && this.productCount > 0 && !this.deleteInProgress;
  }

  loadProductCount(): void {
    if (!this.storeId) {
      return;
    }

    this.countLoading = true;
    this.countError = null;

    this.ecommerceService.getStoreProducts(this.storeId, { page: 1, limit: 1, adminView: true }).subscribe({
      next: (res: any) => {
        let total = 0;

        if (res.response && res.response.success && res.response.data) {
          const pagination = res.response.data.pagination;
          total = pagination?.totalProducts || 0;
        } else if (res.code === 200 && res.data) {
          total = res.data.pagination?.totalProducts || 0;
        } else {
          this.countError = 'Could not load product count for this store.';
          this.productCount = 0;
          this.countLoading = false;
          return;
        }

        this.productCount = total;
        this.countLoading = false;
      },
      error: (err: any) => {
        this.countError = err.error?.response?.message || err.message || 'Failed to load product count.';
        this.productCount = 0;
        this.countLoading = false;
      }
    });
  }

  deleteAllProducts(): void {
    Swal.fire({
      title: '⚠️ PERMANENT DELETE WARNING ⚠️',
      html: `
        <div style="text-align: left; margin: 20px 0;">
          <p><strong>Are you sure you want to PERMANENTLY DELETE ALL products and categories from this store?</strong></p>
          <p style="color: #dc3545; font-weight: bold;">🚨 This action will:</p>
          <ul style="color: #dc3545;">
            <li>Completely remove all products from the database</li>
            <li>Completely remove all categories from the database</li>
            <li>Cannot be undone or recovered</li>
            <li>Delete all product data, images, and settings</li>
          </ul>
          <p style="color: #dc3545;">This will permanently delete ALL products AND all categories for this store. This action cannot be undone.</p>
          <p><strong>Continue only if you are absolutely certain!</strong></p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, I understand the risks',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      focusCancel: true,
      allowEscapeKey: true,
      allowEnterKey: false,
      customClass: {
        popup: 'swal-wide'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.confirmDeleteAllProducts();
      }
    });
  }

  private confirmDeleteAllProducts(): void {
    Swal.fire({
      title: 'Final Confirmation Required',
      html: `
        <div style="text-align: left; margin: 20px 0;">
          <p>To confirm PERMANENT DELETION of ALL products, please type the following text exactly:</p>
          <p style="background: #f8f9fa; padding: 10px; font-family: monospace; border: 1px solid #dee2e6; border-radius: 4px;">
            PERMANENTLY DELETE ALL PRODUCTS AND CATEGORIES
          </p>
        </div>
      `,
      input: 'text',
      inputPlaceholder: 'Type the confirmation text here...',
      icon: 'error',
      showCancelButton: true,
      confirmButtonText: 'Delete All Products & Categories',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      focusCancel: true,
      allowEscapeKey: true,
      allowEnterKey: false,
      inputValidator: (value) => {
        if (value !== 'PERMANENTLY DELETE ALL PRODUCTS AND CATEGORIES') {
          return 'Confirmation text did not match exactly. Action cancelled for safety.';
        }
        return null;
      },
      customClass: {
        popup: 'swal-wide'
      }
    }).then((result) => {
      if (result.isConfirmed && result.value === 'PERMANENTLY DELETE ALL PRODUCTS AND CATEGORIES') {
        this.performDeleteAllProducts();
      }
    });
  }

  private performDeleteAllProducts(): void {
    this.deleteInProgress = true;

    Swal.fire({
      title: 'Deleting All Products & Categories',
      html: `
        <div class="text-center">
          <div class="spinner-border text-danger mb-3" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p>Please wait while all products and categories are being deleted...</p>
          <small class="text-muted">This may take a few moments depending on the number of products.</small>
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      customClass: {
        popup: 'swal-wide'
      }
    });

    let productsDeletedCount = 0;

    this.ecommerceService.deleteAllProducts(this.storeId).pipe(
      switchMap((res: any) => {
        console.log('Delete all products response:', res);
        if (res.code === 200 && res.data) {
          productsDeletedCount = res.data.deletedCount || 0;
        } else if (res.response && res.response.success && res.response.data) {
          productsDeletedCount = res.response.data.deletedCount || 0;
        }
        return this.ecommerceService.deleteAllCategories(this.storeId);
      })
    ).subscribe({
      next: (catRes: any) => {
        console.log('Delete all categories response:', catRes);

        let categoriesDeletedCount = 0;
        if (catRes.response && catRes.response.success && catRes.response.data) {
          categoriesDeletedCount = catRes.response.data.deletedCount || 0;
        } else if (catRes.code === 200 && catRes.data) {
          categoriesDeletedCount = catRes.data.deletedCount || 0;
        }

        this.deleteInProgress = false;

        Swal.fire({
          title: 'All Products & Categories Deleted!',
          html: `
            <div class="text-center">
              <div class="mb-3">
                <i class="fas fa-check-circle text-success" style="font-size: 48px;"></i>
              </div>
              <p><strong>${productsDeletedCount} products</strong> and <strong>${categoriesDeletedCount} categories</strong> have been permanently deleted from the store.</p>
              <small class="text-muted">The product catalog and all categories have been completely cleared.</small>
            </div>
          `,
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745',
          customClass: {
            popup: 'swal-wide'
          }
        }).then(() => {
          this.loadProductCount();
        });
      },
      error: (error: any) => {
        this.deleteInProgress = false;
        console.error('Failed to delete all products/categories:', error);

        Swal.fire({
          title: 'Delete Failed',
          html: `
            <div class="text-center">
              <div class="mb-3">
                <i class="fas fa-exclamation-triangle text-danger" style="font-size: 48px;"></i>
              </div>
              <p><strong>Failed to delete products and categories:</strong></p>
              <p class="text-danger">${error.error?.response?.message || error.message || 'An unexpected error occurred'}</p>
              <small class="text-muted">Please try again or contact support if the problem persists.</small>
            </div>
          `,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545',
          customClass: {
            popup: 'swal-wide'
          }
        });
      }
    });
  }
}
